import { Client } from '../store/clients/interfaces';
import { Filters } from '../store/interfaces';

const getClientFullName = (client: Client) => {
  const fullName = [client.firstName, client.lastName].filter(Boolean).join(' ').trim();
  return fullName || '-';
};

const localeCompareOptions: Intl.CollatorOptions = { sensitivity: 'base', numeric: true };

const compareStrings = (a: string, b: string) =>
  a.localeCompare(b, 'es', localeCompareOptions);

const compareNumbers = (a: number, b: number) => a - b;

const getSortValue = (client: Client, sortBy: string): string | number => {
  switch (sortBy) {
    case 'first_name':
      return getClientFullName(client).toLowerCase();
    case 'last_name': {
      const last = (client.lastName || '').toLowerCase();
      const first = (client.firstName || '').toLowerCase();
      return `${last}\0${first}`;
    }
    case 'email':
      return (client.email || '').toLowerCase();
    case 'merchant_client_id':
      return (client.merchantClientId || client.clientId || '').toLowerCase();
    case 'transacted_amount':
      return client.transactedAmount ?? Number.NEGATIVE_INFINITY;
    case 'created_at':
      return client.createdAt ? new Date(client.createdAt).getTime() : 0;
    default:
      return '';
  }
};

export const hasClientSort = (filters?: Filters) =>
  Boolean(filters?.sort_by && filters?.sort_direction);

export const sortClients = (clients: Client[], filters?: Filters): Client[] => {
  const sortBy = filters?.sort_by;
  const sortDirection = filters?.sort_direction;

  if (!sortBy || !sortDirection) {
    return clients;
  }

  const direction = sortDirection === 'desc' ? -1 : 1;

  return [...clients].sort((a, b) => {
    const valueA = getSortValue(a, sortBy);
    const valueB = getSortValue(b, sortBy);
    const result =
      typeof valueA === 'number' && typeof valueB === 'number'
        ? compareNumbers(valueA, valueB)
        : compareStrings(String(valueA), String(valueB));

    return direction * result;
  });
};

export const paginateClients = (
  clients: Client[],
  page: number,
  size: number
): { data: Client[]; page: number; size: number; totalElements: number; totalPages: number } => {
  const totalElements = clients.length;
  const totalPages = Math.max(Math.ceil(totalElements / size), 1);
  const start = page * size;

  return {
    data: clients.slice(start, start + size),
    page,
    size,
    totalElements,
    totalPages,
  };
};
